import { i18n } from "@/i18n";
import { DateTime } from "luxon";
import type { RoundUnit } from "./SimulationRoundItem";

const ROUND_UNITS = [
  "century",
  "decade",
  "year",
  "halfYear",
  "quarter",
  "month",
  "week",
  "day",
  "hour",
  "minute",
  "second"
];
const ROUND_UNITS_INDEXED = Object.fromEntries(
  ROUND_UNITS.map((val, key) => [val, key])
);

export function getRoundInterval(roundUnit: RoundUnit, roundLength: number) {
  const adjustedRoundLength = roundLength;
  switch (roundUnit) {
    case "second":
      return {
        seconds: adjustedRoundLength
      };
    case "minute":
      return {
        minutes: adjustedRoundLength
      };
    case "hour":
      return {
        hours: adjustedRoundLength
      };
    case "day":
      return {
        days: adjustedRoundLength
      };
    case "week":
      return {
        weeks: adjustedRoundLength
      };
    case "month":
      return {
        months: adjustedRoundLength
      };
    case "quarter":
      return {
        months: 3 * adjustedRoundLength
      };
    case "halfYear":
      return {
        months: 6 * adjustedRoundLength
      };
    case "year":
      return {
        years: adjustedRoundLength
      };
    case "decade":
      return {
        years: 10 * adjustedRoundLength
      };
    case "century":
      return {
        years: 100 * adjustedRoundLength
      };

    default:
      return {
        rounds: adjustedRoundLength
      };
  }
}

/**
 * Return the duration of one
 */
export function getRoundAmountFromTime(
  roundUnit: RoundUnit,
  roundLength: number,
  dateStringStart: string,
  dateStringEnd: string
) {
  const dateEnd = DateTime.fromISO(dateStringEnd).toUTC();
  const dateStart = DateTime.fromISO(dateStringStart).toUTC();

  switch (roundUnit) {
    case "century":
      return (
        Math.floor(
          dateEnd.diff(dateStart, "years").as("years") / (100 * roundLength)
        ) + 1
      );
    case "decade":
      return (
        Math.floor(
          dateEnd.diff(dateStart, "years").as("years") / (10 * roundLength)
        ) + 1
      );
    case "year":
      return (
        Math.floor(dateEnd.diff(dateStart, "years").as("years") / roundLength) +
        1
      );
    case "halfYear":
      return (
        Math.floor(
          dateEnd.diff(dateStart, "months").as("months") / (6 * roundLength)
        ) + 1
      );
    case "quarter":
      return (
        Math.floor(
          dateEnd.diff(dateStart, "months").as("months") / (3 * roundLength)
        ) + 1
      );
    case "month":
      return (
        Math.floor(
          dateEnd.diff(dateStart, "months").as("months") / roundLength
        ) + 1
      );
    case "week":
      return (
        Math.floor(dateEnd.diff(dateStart, "weeks").as("weeks") / roundLength) +
        1
      );
    case "day":
      return (
        Math.floor(dateEnd.diff(dateStart, "days").as("days") / roundLength) + 1
      );
    case "hour":
      return (
        Math.floor(dateEnd.diff(dateStart, "hours").as("hours") / roundLength) +
        1
      );
    case "minute":
      return (
        Math.floor(
          dateEnd.diff(dateStart, "minutes").as("minutes") / roundLength
        ) + 1
      );
    case "second":
      return (
        Math.floor(
          dateEnd.diff(dateStart, "seconds").as("seconds") / roundLength
        ) + 1
      );
    default:
      throw new Error(`no difference config for ${roundUnit}`);
  }
}

/**
 * @param timeStart ISO-formatted string
 */
export function getMinEndTime(roundUnit: RoundUnit, timeStart: string) {
  const d = DateTime.fromISO(timeStart).toUTC();
  const endOfRange = moveToEndOfRange(roundUnit, d);
  const isoTime = endOfRange.toISO();
  if (isoTime == null) {
    throw new Error("Could not convert to ISO time");
  }
  return isoTime;
}

/**
 *
 */
export function moveToEndOfRange(roundUnit: RoundUnit, dateTime: DateTime) {
  if (!DateTime.isDateTime(dateTime)) {
    throw new Error("date is not a DateTime");
  }
  dateTime = dateTime.toUTC();
  switch (roundUnit) {
    case "century":
      return dateTime.plus({ years: 99 }).endOf("year");
    case "decade":
      return dateTime.plus({ years: 9 }).endOf("year");
    case "year":
      return dateTime.plus({ months: 11 }).endOf("year");
    case "halfYear":
      return dateTime.plus({ months: 5 }).endOf("month");
    case "quarter": {
      return dateTime.plus({ months: 2 }).endOf("month");
    }
    case "month": {
      return dateTime.endOf("month");
    }
    case "week":
      return dateTime.plus({ days: 6 }).endOf("day");
    case "day":
      return dateTime.endOf("day");
    case "hour":
      return dateTime.plus({ minutes: 59 }).endOf("hour");
    case "minute":
      return dateTime.plus({ seconds: 59 }).endOf("minute");
    case "second":
    default:
      return dateTime;
  }
}

/**
 *
 */
export function isYearUnit(roundUnit: RoundUnit) {
  return ["century", "decade", "year", "halfYear", "quarter", "month"].includes(
    roundUnit
  );
}

/**
 *
 */
export function isTimeUnit(roundUnit: RoundUnit) {
  return ["hour", "minute", "second"].includes(roundUnit);
}

/**
 *
 */
export function dateToAbsoluteParts(date: string | Date) {
  if (typeof date === "string") date = new Date(date);
  const month = date.getUTCMonth() + 1;
  let quarter = 1;
  if (month <= 12) quarter = 4;
  if (month <= 9) quarter = 3;
  if (month <= 6) quarter = 2;
  if (month <= 3) quarter = 1;
  const halfYear = quarter <= 2 ? 1 : 2;
  return {
    year: date.getUTCFullYear(),
    halfYear: halfYear,
    quarter: quarter,
    month: month,
    date: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
    second: date.getUTCSeconds()
  };
}

/**
 *
 */
export function moveDateToUnit(
  roundUnit: RoundUnit,
  roundUnitFrom: RoundUnit,
  date: string | Date,
  timeType: "start" | "end" = "start"
): string {
  const dateParts = dateToAbsoluteParts(date);
  const { year } = dateParts;
  let { month, date: day, hour, minute, second } = dateParts;

  // transform DOWN (eg. year -> halfYear)
  if (timeType === "end" && unitSmallerThan(roundUnit, roundUnitFrom)) {
    // MONTH (incl. QUARTER and HALFYEAR)
    if (isSmallerOrEqual(roundUnit, "month")) {
      if (roundUnitFrom == "quarter") {
        if (month <= 3) month = 3;
        else if (month <= 6) month = 6;
        else if (month <= 9) month = 9;
        else if (month <= 12) month = 12;
      } else if (roundUnitFrom == "halfYear") {
        if (month <= 6) month = 6;
        else month = 12;
      } else if (unitGreaterThan(roundUnitFrom, "halfYear")) {
        month = 12;
      }
    } else if (roundUnit === "quarter") {
      if (isSmallerOrEqual(roundUnitFrom, "halfYear")) {
        if (month <= 6) month = 1;
        else month = 10;
      } else {
        month = 10;
      }
    } else if (roundUnit === "halfYear") {
      month = 7;
    }

    // DAY, HOUR, MINUTE, SECOND
    if (
      isSmallerOrEqual(roundUnit, "day") &&
      unitGreaterThan(roundUnitFrom, "week")
    ) {
      day = DateTime.utc(year, month).endOf("month").day;
    }
    if (isSmallerOrEqual(roundUnit, "hour")) {
      hour = 23;
    }
    if (isSmallerOrEqual(roundUnit, "minute")) {
      minute = 59;
    }
    if (isSmallerOrEqual(roundUnit, "second")) {
      second = 59;
    }
  }
  // transform UP (eg. halfYear -> year)
  if (unitGreaterThan(roundUnit, roundUnitFrom)) {
    if (roundUnit === "halfYear") {
      if (month > 6) month = 7;
      else month = 1;
    } else if (roundUnit === "quarter") {
      if (month <= 3) month = 1;
      else if (month <= 6) month = 4;
      else if (month <= 9) month = 7;
      else if (month <= 12) month = 10;
    }
  }

  if (unitGreaterThan(roundUnit, "halfYear")) {
    month = 1;
  }
  if (unitGreaterThan(roundUnit, "day")) {
    day = 1;
  }
  if (!isTimeUnit(roundUnit)) {
    hour = 0;
    minute = 0;
    second = 0;
  }

  let dateTime = DateTime.utc(year, month, day, hour, minute, second, 0);
  if (timeType == "end") {
    if (["century", "decade", "year"].includes(roundUnit)) {
      dateTime = dateTime.endOf("year");
    } else if (["halfYear", "quarter", "month"].includes(roundUnit)) {
      dateTime = dateTime.endOf("month");
    } else if (["week", "day"].includes(roundUnit)) {
      dateTime = dateTime.endOf("day");
    } else if (roundUnit === "hour") {
      dateTime = dateTime.endOf("hour");
    } else if (roundUnit === "minute") {
      dateTime = dateTime.endOf("minute");
    } else if (roundUnit === "second") {
      dateTime = dateTime.endOf("second");
    }
  }

  const isoDate = dateTime.toISO();

  if (isoDate == null) {
    throw new Error("Could not convert date to ISO string");
  }

  return isoDate;
}

/**
 *
 */
export function getEndTimeInDuration(
  timeStart: string | null,
  roundUnit: RoundUnit,
  roundLength: number,
  roundAmount: number
) {
  if (!timeStart) return null;
  if (roundUnit == "round") return null;
  const d = DateTime.fromISO(timeStart).toUTC();
  const endOfRange = moveToEndOfRange(roundUnit, d);
  const durationInterval = getRoundInterval(
    roundUnit,
    roundLength * (roundAmount - 1)
  );
  const endTime = endOfRange.plus(durationInterval);
  return endTime.toISO();
}

/**
 *
 * @param timeStart ISO-formatted date string
 */
export function formatRoundName(
  durationType: "roundAmount" | "timeframe",
  roundUnit: RoundUnit,
  roundLength: number,
  roundIndex: number,
  timeStart: string | null,
  includeIndex = true
) {
  if (typeof roundIndex === "string") roundIndex = parseInt(roundIndex, 10);
  const roundName = i18n.t(`roundUnits.${roundUnit}`);
  if (durationType === "roundAmount" || timeStart == null) {
    return `${roundName} ${roundIndex + 1}`;
  } else {
    const roundDate = getRoundStartDate(
      roundUnit,
      roundLength,
      roundIndex,
      timeStart
    );
    const formattedDate = formatRoundDate(roundUnit, roundDate.toJSDate());
    if (!includeIndex) return formattedDate;
    return `${roundIndex + 1} – ${formattedDate}`;
  }
}

/**
 *
 * @param simulationTimeStart ISO-formatted datetime string
 */
export function getRoundStartDate(
  roundUnit: RoundUnit,
  roundLength: number,
  roundIndex: number,
  simulationTimeStart: string
) {
  const interval = getRoundInterval(roundUnit, roundLength * roundIndex);
  const simulationStartDateTime = DateTime.fromISO(simulationTimeStart).toUTC();
  return simulationStartDateTime.plus(interval);
}

export function roundIndexInSeconds(roundUnit: RoundUnit, roundLength: number, roundIndex: number) {
  // Since seconds are user-presented, start counting at 1.
  roundIndex = roundIndex + 1;
  
  switch (roundUnit) {
    case "round": return roundIndex;
    case "second": return roundIndex * roundLength;
    case "minute": return roundIndex * roundLength * 60;
    case "hour": return roundIndex * roundLength * 60 * 60;
    case "day": return roundIndex * roundLength * 60 * 60 * 24;
    case "week": return roundIndex * roundLength * 60 * 60 * 24 * 7;
    case "month": return roundIndex * roundLength * 60 * 60 * 24 * 30;
    case "quarter": return roundIndex * roundLength * 60 * 60 * 24 * 30 * 3;
    case "halfYear": return roundIndex * roundLength * 60 * 60 * 24 * 30 * 6;
    case "year": return roundIndex * roundLength * 60 * 60 * 24 * 365;
    case "decade": return roundIndex * roundLength * 60 * 60 * 24 * 365 * 10;
    case "century": return roundIndex * roundLength * 60 * 60 * 24 * 365 * 100;
  }
}

/**
 *
 */
export function formatRoundDate(roundUnit: RoundUnit, dateToFormat: Date) {
  const {
    year,
    halfYear,
    quarter,
    month,
    date: day,
    hour,
    minute,
    second
  } = dateToAbsoluteParts(dateToFormat);
  switch (roundUnit) {
    case "century":
    case "decade":
    case "year":
      return `${year}`;
    case "halfYear":
      return `${i18n.t("dateParts.halfYear", { halfYear, year })}`;
    case "quarter":
      return `${i18n.t("dateParts.quarter", { quarter, year })}`;
    case "month": {
      return `${month.toString().padStart(2, "0")}/${year}`;
    }
    case "week":
    case "day":
      return buildDateString(year, month, day);
    case "hour":
      return `${buildDateString(year, month, day)} ${buildTimeString(hour)}`;
    case "minute":
      return `${buildDateString(year, month, day)} ${buildTimeString(
        hour,
        minute
      )}`;
    case "second":
      return `${buildDateString(year, month, day)} ${buildTimeString(
        hour,
        minute,
        second
      )}`;
  }
}

/**
 *
 */
function buildDateString(year: number, month = 1, day = 1) {
  return `${year}-${month.toString().padStart(2, "0")}-${day
    .toString()
    .padStart(2, "0")}`;
}

function buildTimeString(hour = 0, minute = 0, second: number | null = null) {
  const baseTime = `${hour.toString().padStart(2, "0")}:${minute
    .toString()
    .padStart(2, "0")}`;
  if (second == null) {
    return baseTime;
  } else {
    return baseTime + `:${second.toString().padStart(2, "0")}`;
  }
}

/**
 * Checks if a time unit is smaller or equal than another time unit
 * (e.g. "second" IS smaller than "minute")
 */
function isSmallerOrEqual(roundUnit: RoundUnit, baseRoundUnit: RoundUnit) {
  return ROUND_UNITS_INDEXED[roundUnit] >= ROUND_UNITS_INDEXED[baseRoundUnit];
}
/**
 * Checks if a time unit is smaller than another time unit
 * (e.g. "second" IS smaller than "minute")
 */
function unitSmallerThan(roundUnit: RoundUnit, baseRoundUnit: RoundUnit) {
  return ROUND_UNITS_INDEXED[roundUnit] > ROUND_UNITS_INDEXED[baseRoundUnit];
}
/**
 * Checks if a time unit is greater than another time unit
 */
function unitGreaterThan(roundUnit: RoundUnit, baseRoundUnit: RoundUnit) {
  return ROUND_UNITS_INDEXED[roundUnit] < ROUND_UNITS_INDEXED[baseRoundUnit];
}
